import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";

import { THEME_COOKIE, type ThemeValue } from "@/lib/theme";
import { cn } from "@/lib/utils";

const OPTIONS: { value: ThemeValue; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

type ToggleProps = {
  className?: string;
};

function readCookie(): ThemeValue {
  if (typeof document === "undefined") return "system";
  const m = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE}=([^;]+)`));
  if (!m) return "system";
  const v = decodeURIComponent(m[1]!);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function applyTheme(value: ThemeValue) {
  const root = document.documentElement;
  let resolved: "light" | "dark" = "light";
  if (value === "system") {
    resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } else {
    resolved = value;
  }
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

function persistCookie(value: ThemeValue) {
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${THEME_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${oneYear}; samesite=lax`;
}

export function ThemeToggle({ className }: ToggleProps) {
  const [theme, setTheme] = useState<ThemeValue>("system");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTheme(readCookie());
    setMounted(true);
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (readCookie() === "system") applyTheme("system");
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  function pick(value: ThemeValue) {
    setTheme(value);
    persistCookie(value);
    applyTheme(value);
  }

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        "inline-flex h-8 items-center gap-0.5 rounded-full border border-border bg-card p-0.5",
        className,
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = mounted && theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => pick(value)}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors",
              active ? "bg-foreground text-background" : "hover:text-foreground",
            )}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}
